import { useState, type ReactNode } from "react";
import { ArrowLeft, LogOut, Settings, User as UserIcon } from "lucide-react";
import type { User } from "../lib/api.ts";
import { useConnectionState } from "../lib/relayContext.tsx";
import { navigate, useRoute } from "../lib/router.ts";
import { lastProject } from "../lib/projectContext.ts";
import { projectPath } from "../lib/routes.ts";
import { ConfirmDialog } from "./Dialog.tsx";
import { IconButton } from "./IconButton.tsx";
import { Menu } from "./Menu.tsx";

interface Props {
  user: User;
  onLogout: () => void;
  /** Current context (Machine › Project breadcrumb). */
  context?: ReactNode;
  /** Page-specific controls, e.g. panel toggles in a Project. */
  actions?: ReactNode;
}

/** Global header: branding, where you are, connection status, settings and account. Nothing that Explorer/Agent already offer. */
export function Header({ user, onLogout, context, actions }: Props) {
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const conn = useConnectionState();
  const route = useRoute();
  // Outside a Project and outside Home, offer a direct way back to the one the user was working in.
  const back = route.name === "project" || route.name === "home" ? null : lastProject();
  return (
    <header className="header">
      <button className="brand" onClick={() => navigate("/")} title="Home" data-testid="brand">
        <img src="/favicon-32x32.png" alt="" className="brand-logo" width="16" height="16" />
        racler<span className="brand-dev">.dev</span>
      </button>
      {back && (
        <button
          className="back-to-project"
          onClick={() => navigate(projectPath(back.machineId, back.projectId, back.chatId))}
          title="Return to the Project you were working in"
          data-testid="back-to-project"
        >
          <ArrowLeft size={14} aria-hidden /> Back to project
        </button>
      )}
      <div className="header-context">{context}</div>
      <div className="header-actions">
        {actions}
        <span
          className={`dot ${conn === "open" ? "on" : "off"}`}
          title={conn === "open" ? "Connected to racler.dev server" : `Server connection: ${conn}`}
          data-testid="relay-status"
        />
        <IconButton
          icon={Settings}
          label="Settings"
          onClick={() => navigate("/settings")}
          testId="settings"
        />
        <Menu
          testId="account-menu"
          trigger={
            <>
              <UserIcon size={15} strokeWidth={1.75} aria-hidden />
              <span className="account-name">{user.displayName}</span>
            </>
          }
          items={[
            {
              label: "Sign out",
              onSelect: () => setConfirmSignOut(true),
              testId: "sign-out",
              icon: <LogOut size={13} aria-hidden />,
            },
          ]}
        />
      </div>
      {confirmSignOut && (
        <ConfirmDialog
          title="Sign out?"
          message="Are you sure you want to sign out?"
          confirmLabel="Sign out"
          danger
          onConfirm={async () => {
            onLogout();
          }}
          onClose={() => setConfirmSignOut(false)}
        />
      )}
    </header>
  );
}
